import { OOIManifestBuilder } from '@wix/editor-platform-builder-manifest-adapter';
import type {
  CheckboxItemType,
  ElementsPanelProps,
} from '@wix/editor-platform-feature-store';
import { ManifestComponentExtensionType } from '@wix/editor-platform-manifest-services';
import type {
  ElementsPanelData,
  ResolvedOOIWidgetStatics,
} from '@wix/editor-platform-ooi-metadata-resolver';
import type { ResolverApis, ResolverByExtensionType } from '../types';

const noop = () => {};

const mapToElementsPanel = (
  data?: ElementsPanelData<any>,
): ElementsPanelProps['items'] =>
  (data ?? []).map((value) => {
    const isGrouping = 'type' in value && value.type === 'grouping';
    const key = 'key' in value ? value.key : null;

    if (!key && !isGrouping) {
      return null;
    }

    const children = (value.children ?? []) as ElementsPanelData<any>;

    return {
      displayName: value.displayName,
      id: isGrouping ? value.displayName : key,
      tooltipData: value.description ?? '',
      isRemovable: !isGrouping,
      ...(children.length
        ? {
            children: mapToElementsPanel(children),
          }
        : {}),
    };
  }) as ElementsPanelProps['items'];

const collectValuesData = (
  items: CheckboxItemType[],
  metadata: ResolvedOOIWidgetStatics,
  apis: ResolverApis,
) => {
  const { innerElements, ...rest } = metadata;
  const manifestWithDefaultValues = new OOIManifestBuilder({
    statics: rest,
    nativeSettingsPanelUrl: null,
    layout: null,
  }).build({
    isMobile: false,
  });
  const compRef = apis.editorPointerAPI.getDocumentPointer(apis.editorPointer);
  const booleansStyles =
    apis.documentServicesAPI.tpa.style.getByComp(compRef).style.booleans;

  const collectValues = (
    items: CheckboxItemType[],
  ): Record<string, boolean> => {
    return items.reduce((acc, val) => {
      const children = val.children?.length ? collectValues(val.children) : {};

      if (!val.isRemovable) {
        return { ...acc, ...children };
      }
      const dmKey = metadata.styleParams?.[val.id]?.key ?? val.id;

      const valueFromDM = booleansStyles[dmKey];
      const defaultValueFromManifest =
        manifestWithDefaultValues.editorElement.cssCustomProperties?.[val.id]
          ?.defaultValue;

      const itemValue =
        typeof valueFromDM === 'boolean'
          ? valueFromDM
          : defaultValueFromManifest;

      return {
        ...acc,
        [val.id]: itemValue ?? false,
        ...children,
      };
    }, {});
  };

  return collectValues(items);
};

export const getOOIWidgetElementsPanelProps: ResolverByExtensionType<
  ManifestComponentExtensionType.OOI
> = ({ metadata }, apis): ElementsPanelProps => {
  if (!metadata || !metadata.statics.elementsPanel) {
    throw new Error(
      'Metadata was not provided to the OOI elements panel adapter',
    );
  }

  const elementsPanel = metadata.statics.elementsPanel;

  const { editorPointer, editorPointerAPI, documentServicesAPI } = apis;

  const items = mapToElementsPanel(elementsPanel);

  const data = collectValuesData(items, metadata.statics, apis);

  const handleToggleState: ElementsPanelProps['onToggleState'] = async (
    id,
    newValue,
  ) => {
    const dmKey = metadata.statics.styleParams?.[id]?.key ?? id;
    const compRef = editorPointerAPI.getDocumentPointer(editorPointer);

    return new Promise((resolve) => {
      documentServicesAPI.tpa.style.setCompStyleParam(
        compRef,
        [{ key: dmKey, type: 'boolean', param: { value: newValue } }],
        () => {
          resolve({ [id]: newValue });
        },
      );
    });
  };

  return {
    items,
    data,
    onToggleState: handleToggleState,
    highlightComponent: noop,
    removeHighlight: noop,
  };
};
